import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, Instagram, Facebook, Phone } from 'lucide-react';
import CategoryHero from './CategoryHero';
import NewsTicker from './NewsTicker';
import FilterChip from './FilterChip';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem, AdminAddBar } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

// The platform datatable declares this multiselect as a `string` column, so a
// saved array comes back comma-joined ("Dance Schools,Choreographers") rather
// than as an array. Split it back apart — an `Array.isArray` check fails on a
// string, and a plain `===` compare can never match a multi-value pick.
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}


export const MEDIA_TYPES = ['TV Channels', 'Radio Stations', 'RJ', 'Emcees'] as const;

const MEDIA_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Channel name', required: true },
  { key: 'type', label: 'Type', type: 'multiselect', options: [...MEDIA_TYPES] },
  { key: 'description', label: 'Description' },
  { key: 'contactUrl', label: 'Contact URL (Instagram/Facebook/Website)', type: 'url' },
  { key: 'instagram', label: 'Instagram URL', type: 'url' },
  { key: 'website', label: 'Website URL', type: 'url' },
  { key: 'phone', label: 'Phone Number', type: 'tel', placeholder: 'e.g. +1 (214) 555-0123' },
  { key: 'image', label: 'Image URL', type: 'image' },
  ...SOCIAL_FIELDS,
];

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&h=400&fit=crop';

export const MEDIA_ITEMS = [
  {
    id: 'maha-tv',
    name: 'MAHA TV',
    type: 'TV Channels',
    instagram: '',
    contactUrl: 'https://www.instagram.com/mahatv_usa/',
    description: 'Telugu News & Broadcast Network',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782508334697-cfkklq.png',
  },
  {
    id: 'fun-asia',
    name: 'Fun Asia',
    type: 'Radio Stations',
    website: '',
    contactUrl: 'https://www.funasia.net/',
    description: 'Radio & Entertainment',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783523787939-egaf8a.png',
    imageFit: 'contain',
  },
  {
    id: 'anya-ahuja',
    name: 'Anya Ahuja',
    type: ['RJ', 'Emcees'],
    instagram: '',
    contactUrl: 'https://www.instagram.com/rjanyaahuja?igsh=MXUzNnA2N3d6aGg0cA%3D%3D&utm_source=qr',
    description: 'RJ Radio Rangrez · Dallas',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-08/45fe895542ae.jpeg',
  },
  {
    id: 'gargi-duggal',
    name: 'Gargi Duggal',
    type: ['Emcees'],
    instagram: '',
    contactUrl: 'https://www.instagram.com/gargiduggal/',
    description: 'Emcee · Dallas',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784539427388-g2ijc8.png',
  },
  {
    id: 'vaibhav-sheth',
    name: 'Vaibhav Sheth',
    type: ['RJ'],
    instagram: '',
    contactUrl: 'https://www.instagram.com/vaibhav_wav',
    description: 'RJ Radio Rangrez',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/079bb0a94a2f.jpeg',
  },
];

export function MediaCard({ item, index }: { item: typeof MEDIA_ITEMS[0]; index: number }) {
  // The big button always points at contactUrl — it is not derived from the
  // dedicated instagram/website/facebook fields. Whether it renders as
  // Instagram, Facebook, or a generic Website button is just cosmetic,
  // based on what domain contactUrl happens to be.
  const linkUrl = (item as any).contactUrl && (item as any).contactUrl !== '#' ? (item as any).contactUrl : null;
  const isFacebookContact = !!linkUrl && linkUrl.includes('facebook.com');
  const isInstagramContact = !!linkUrl && linkUrl.includes('instagram.com');

  const phone = typeof (item as any).phone === 'string' ? (item as any).phone.trim() : '';

  const bigButton = (() => {
    if (!linkUrl) {
      if (phone) {
        return (
          <a
            href={`tel:${phone.replace(/[^+\d]/g, '')}`}
            className="w-full flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
            style={{ backgroundColor: '#51ba4f' }}
          >
            <Phone className="w-3 h-3" /> Call
          </a>
        );
      }
      return (
        <span
          className="w-full flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md"
          style={{ backgroundColor: '#082f6d' }}
        >
          <Globe className="w-3 h-3" /> Visit
        </span>
      );
    }
    if (isFacebookContact) {
      return (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
          style={{ backgroundColor: '#1877F2' }}
        >
          <Facebook className="w-3 h-3" /> Facebook
        </a>
      );
    }
    if (isInstagramContact) {
      return (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
          style={{
            background:
              'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
          }}
        >
          <Instagram className="w-3 h-3" /> Instagram
        </a>
      );
    }
    return (
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
        style={{ backgroundColor: '#082f6d' }}
      >
        <Globe className="w-3 h-3" /> Visit
      </a>
    );
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col group"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="relative w-full h-40 sm:h-44 overflow-hidden bg-black">
        <img
          src={item.image}
          alt={item.name}
          className={`w-full h-full ${(item as any).imageFit === 'contain' ? 'object-contain p-3' : 'object-cover object-top'} group-hover:scale-105 transition-transform duration-500`}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="flex flex-col flex-1 px-3.5 py-3 gap-1.5">
        <h3 className="font-semibold text-gray-900 leading-snug text-[14px]">
          <Link
            to={`/media/${slugify(item.name)}`}
            className="hover:text-[#082f6d] hover:underline transition-colors"
          >
            {item.name}
          </Link>
        </h3>
        {item.description && (
          <p className="text-gray-500 leading-relaxed text-[12px]">{item.description}</p>
        )}
        <div className="mt-auto pt-1.5 flex flex-col gap-1.5">
          {bigButton}
          <SocialLinks item={item} px={22} />
        </div>
      </div>
    </motion.div>
  );
}

export default function Media() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState<string | null>(null);
  const mc = useManagedCollection<typeof MEDIA_ITEMS[0]>('media', MEDIA_ITEMS, (m) => slugify(m.name));

  const filteredItems = mc.items.filter((item) =>
    (searchQuery.trim() === '' ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))) &&
    (!activeType || toTypeArray((item as any).type).includes(activeType))
  );



  return (
    <div className="min-h-screen bg-white">
      <CategoryHero title="Media" placeholder="Search Media…" />
      <NewsTicker />

      <section className="bg-gray-50/50 py-6">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-[16px] md:text-[18px] font-bold text-gray-900"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Media
            </h2>
          </div>

          <div className="mb-4">
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-2.5" style={{ letterSpacing: '0.12em' }}>Explore by Type</p>
            <div className="flex gap-2 flex-wrap items-center">
              {MEDIA_TYPES.map((t) => (
                <FilterChip key={t} active={activeType === t} color="#082f6d" onClick={() => setActiveType(activeType === t ? null : t)}>{t}</FilterChip>
              ))}
              {activeType && (
                <button
                  onClick={() => setActiveType(null)}
                  className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 border border-dashed border-gray-300 rounded-full px-3 py-1.5 transition-colors"
                >
                  ✕ Clear Filter
                </button>
              )}
            </div>
          </div>

          <AdminAddBar mc={mc} fields={MEDIA_FIELDS} label="Add Media Channel" />

          {filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-[15px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                No media channels found.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
              {filteredItems.map((item, i) => (
                <AdminItem key={item._key} mc={mc} item={item} fields={MEDIA_FIELDS} title="Media Channel">
                  <MediaCard item={item as any} index={i} />
                </AdminItem>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
